import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, Loader2, Clock, Monitor, Crop, Box, Layers, Plus, Volume2, VolumeX, RefreshCw,
  ChevronDown, Folder, BarChart3, RectangleHorizontal, RectangleVertical, Square, Scan, Maximize,
  Link as LinkIcon,
} from "lucide-react";
import { Sparkle } from "@/components/icons";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { classifyRef, useRefAnalysis, RefWarnings, AutoTag } from "@/components/RefTools";

const DURATIONS = [4, 5, 6, 8, 10, 15, 30];
const MODEL_MAX_DUR = { "seedance-2-5": 30, "seedance-2-0": 15, "seedance-2-0-fast": 15, "seedance-2-0-mini": 15 };
const RESOLUTIONS = ["480p", "720p", "1080p"];
const RATIOS = ["Auto", "16:9", "9:16", "1:1", "4:3", "21:9"];
const REF_TYPES = ["character", "style", "environment", "prop", "pose", "camera", "lighting", "color_palette"];
const MODELS = [
  { id: "seedance-2-5", label: "SeeDance 2.5" },
  { id: "seedance-2-0", label: "SeeDance 2.0" },
  { id: "seedance-2-0-fast", label: "SeeDance 2.0 Fast" },
  { id: "seedance-2-0-mini", label: "SeeDance 2.0 Mini" },
];
const RES_MULT = { "480p": 0.6, "720p": 1.0, "1080p": 1.6 };
const MODEL_MULT = { "seedance-2-5": 1.0, "seedance-2-0": 1.0, "seedance-2-0-fast": 0.7, "seedance-2-0-mini": 0.5 };
const cost = (d, r, m) => Math.max(1, Math.round(d * 10 * (RES_MULT[r] || 1) * (MODEL_MULT[m] || 1)));
const RATIO_ICONS = { "Auto": Scan, "21:9": RectangleHorizontal, "16:9": RectangleHorizontal, "4:3": RectangleHorizontal, "1:1": Square, "9:16": RectangleVertical };

function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-3 text-sm font-heading border rounded-md transition-colors ${active ? "border-gold text-gold bg-[#1a140d]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
      {children}
    </button>
  );
}
function RatioChip({ active, onClick, label, testid }) {
  const Icon = RATIO_ICONS[label] || Square;
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex-1 flex flex-col items-center gap-1.5 py-3 border rounded-md transition-colors ${active ? "border-gold text-gold bg-[#1a140d]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
      <Icon size={20} /><span className="text-xs font-heading">{label}</span>
    </button>
  );
}

export default function VideoGenModal({ open, onOpenChange, projectId, projectTitle, defaultPrompt, defaultRefs, onDone }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [paramsExpanded, setParamsExpanded] = useState(false);
  const [prompt, setPrompt] = useState(defaultPrompt || "");
  const [refs, setRefs] = useState(defaultRefs || []);
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState("720p");
  const [ratio, setRatio] = useState("Auto");
  const [model, setModel] = useState("seedance-2-5");
  const [count, setCount] = useState(1);
  const [audio, setAudio] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const maxDur = MODEL_MAX_DUR[model] || 30;
  const availDurations = DURATIONS.filter((d) => d <= maxDur);
  const modelBadge = (MODELS.find((m) => m.id === model)?.label || "").replace("SeeDance", "").trim() || "2.5";
  useEffect(() => { if (duration > maxDur) setDuration(maxDur); }, [model, maxDur, duration]);

  useEffect(() => {
    if (open) {
      setPrompt(defaultPrompt || "");
      setRefs(defaultRefs || []);
      setParamsExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isAdmin = user?.role === "admin";
  const total = cost(duration, resolution, model) * count;
  const warnings = useRefAnalysis(refs);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files.slice(0, 12 - refs.length)) {
      try {
        const b64 = await new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(f); });
        const { data } = await api.post(`/uploads/image`, { image_base64: b64, origin_url: window.location.origin });
        setRefs((r) => [...r, { url: data.url, type: "character", auto: true }]);
        classifyRef(data.url).then(({ type, label }) => {
          setRefs((r) => r.map((v) => v.url === data.url ? { ...v, type, label, auto: true } : v));
        });
      } catch { toast.error("Upload failed"); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Write a prompt first"); return; }
    setBusy(true);
    try {
      const url = projectId ? `/projects/${projectId}/videos/generate` : `/videos/generate`;
      const { data } = await api.post(url, {
        prompt, duration, resolution, aspect_ratio: ratio, model, count,
        generate_audio: audio, references: refs, image_urls: refs.filter((r) => !r.type).map((r) => r.url), origin_url: window.location.origin,
      });
      onOpenChange(false);
      await refreshUser();
      onDone && onDone();
      const failed = data.videos.filter((v) => v.status === "failed");
      if (failed.length) toast.error(failed[0].error || "Some tasks failed");
      else toast.success(`${data.videos.length} video task(s) started 🎬`);
    } catch (e) {
      if (e.response?.status === 402) { toast.error("Not enough credits."); navigate("/pricing"); }
      else if (e.response?.status === 503) toast.error("Video not configured — add your SeeDance API key.");
      else toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-zinc-800 text-[#F0E9DD] sm:max-w-md max-h-[94vh] overflow-y-auto p-0 gap-0 [&>button]:hidden">
        <DialogDescription className="sr-only">Configure and generate an animated video.</DialogDescription>
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <button onClick={() => (paramsExpanded ? setParamsExpanded(false) : onOpenChange(false))} data-testid="video-modal-close" className="text-gold hover:opacity-70 transition-opacity"><X size={24} /></button>
          {paramsExpanded ? (
            <DialogTitle className="font-heading font-bold text-xl text-white">Generate Parameters</DialogTitle>
          ) : (
            <button onClick={() => setParamsExpanded(true)} className="flex items-center gap-1.5">
              <DialogTitle className="font-heading font-bold text-xl text-white truncate max-w-[220px]">{projectTitle || "Animation"}</DialogTitle>
              <ChevronDown size={20} className="text-gold" />
            </button>
          )}
          {paramsExpanded ? <Sparkle size={22} className="text-gold" /> : <button onClick={() => navigate("/creations")} className="text-gold hover:opacity-70"><Folder size={22} /></button>}
        </div>

        <div className="px-5 space-y-4">
          {paramsExpanded ? (
            <>
              <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
                <div className="label-mono mb-3">Model</div>
                <div className="grid grid-cols-2 gap-2">
                  {MODELS.map((m) => (
                    <button key={m.id} onClick={() => setModel(m.id)} data-testid={`model-${m.id}`}
                      className={`flex flex-col items-center gap-1.5 py-3 border rounded-md transition-colors ${model === m.id ? "border-gold text-gold bg-[#1a140d]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                      <BarChart3 size={18} /><span className="text-xs font-heading text-center leading-tight">{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-3"><span className="label-mono">Duration</span><Clock size={16} className="text-gold" /></div>
                <div className="grid grid-cols-5 gap-2">{availDurations.map((d) => <Chip key={d} active={duration === d} onClick={() => setDuration(d)} testid={`dur-${d}`}>{d}s</Chip>)}</div>
                {model !== "seedance-2-5" && <p className="text-[10px] text-zinc-600 mt-2">SeeDance 2.0 supports up to 15s. Choose 2.5 for clips up to 30s.</p>}
              </div>
              <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-3"><span className="label-mono">Resolution</span><Monitor size={16} className="text-gold" /></div>
                <div className="grid grid-cols-3 gap-2">{RESOLUTIONS.map((r) => <Chip key={r} active={resolution === r} onClick={() => setResolution(r)} testid={`res-${r}`}>{r}</Chip>)}</div>
              </div>
              <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-3"><span className="label-mono">Ratio</span><Crop size={16} className="text-gold" /></div>
                <div className="flex gap-2">{RATIOS.map((r) => <RatioChip key={r} active={ratio === r} onClick={() => setRatio(r)} label={r} testid={`ratio-${r}`} />)}</div>
              </div>
              <button onClick={() => setAudio(!audio)} data-testid="video-audio-toggle" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-gold transition-colors">
                {audio ? <Volume2 size={16} className="text-gold" /> : <VolumeX size={16} />} Background audio {audio ? "on" : "off"}
              </button>
            </>
          ) : (
            <>
              <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40 min-h-[190px] flex flex-col">
                <div className="flex items-center gap-2 text-gold mb-2 font-heading font-semibold"><Sparkle size={16} /> Brainstorm</div>
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="video-prompt"
                  placeholder="Brainstorm your plot here, try typing @ to create"
                  className="w-full flex-1 bg-transparent text-sm focus:outline-none resize-none placeholder:text-zinc-600 leading-relaxed" />
              </div>
              <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-white font-heading font-semibold"><LinkIcon size={16} className="text-gold" /> Refs</div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setRefs([])} data-testid="clear-refs" className="text-zinc-400 hover:text-gold transition-colors"><RefreshCw size={16} /></button>
                    <span className="w-px h-4 bg-zinc-700" />
                    <span className="text-xs font-mono border border-zinc-700 rounded-full px-2.5 py-1 text-gold">{refs.length}/12</span>
                  </div>
                </div>
                <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="video-ref-input" />
                <div className="flex flex-wrap gap-2">
                  {refs.map((r, i) => (
                    <div key={i} className="w-16">
                      <div className="relative w-16 h-16 border border-zinc-800 rounded-md overflow-hidden">
                        <img src={r.url} alt="ref" className="w-full h-full object-cover" />
                        <button onClick={() => setRefs((x) => x.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-ink-950/80 p-0.5"><X size={12} /></button>
                      </div>
                      <select value={r.type || "character"} onChange={(e) => setRefs((x) => x.map((v, j) => j === i ? { ...v, type: e.target.value, auto: false } : v))}
                        data-testid={`video-ref-type-${i}`} className="mt-1 w-16 bg-ink-950 border border-zinc-800 text-[9px] text-zinc-300 rounded px-0.5 py-0.5 focus:outline-none">
                        {REF_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                      </select>
                      {r.auto && <div className="mt-0.5"><AutoTag /></div>}
                    </div>
                  ))}
                  {refs.length < 12 && (
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="video-add-ref" className="w-16 h-16 border border-dashed border-gold/50 rounded-md flex items-center justify-center text-gold hover:bg-[#1a140d] transition-colors">
                      {uploading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={22} />}
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-zinc-600 mt-2">References are auto-classified by AI — override anytime. Character identity is locked; your Brainstorm prompt always controls the story.</p>
                <div className="mt-2"><RefWarnings warnings={warnings} /></div>
              </div>
              <button onClick={() => setParamsExpanded(true)} data-testid="expand-params" className="w-full text-left border border-zinc-800 rounded-xl p-4 bg-zinc-950/40 hover:border-zinc-600 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-heading font-semibold text-gold">Generate Parameters</span>
                  <ChevronDown size={18} className="text-gold" />
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="flex flex-col items-center gap-1"><Maximize size={18} className="text-gold" /><span className="font-heading text-lg text-white leading-none mt-1">{resolution}</span><span className="label-mono">Resolution</span></div>
                  <div className="flex flex-col items-center gap-1"><Clock size={18} className="text-gold" /><span className="font-heading text-lg text-white leading-none mt-1">{duration}s</span><span className="label-mono">Duration</span></div>
                  <div className="flex flex-col items-center gap-1"><Scan size={18} className="text-gold" /><span className="font-heading text-lg text-white leading-none mt-1">{ratio}</span><span className="label-mono">Ratio</span></div>
                  <div className="flex flex-col items-center gap-1"><Box size={18} className="text-gold" /><span className="font-heading text-sm text-white leading-none mt-1">SeeDance</span><span className="label-mono">Model</span><span className="mt-1 text-[10px] text-gold border border-gold/40 rounded px-2 py-0.5 bg-[#1a140d]">{modelBadge}</span></div>
                </div>
              </button>
            </>
          )}
        </div>

        <div className="px-5 pt-4 pb-1 flex items-end gap-3">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-3 border border-zinc-800 rounded-full px-3 py-2">
              <button onClick={() => setCount(Math.max(1, count - 1))} data-testid="count-minus" className="w-7 h-7 rounded-full border border-gold/60 text-gold flex items-center justify-center text-lg leading-none">−</button>
              <span className="flex items-center gap-1.5 text-base"><Layers size={16} className="text-gold" /> {count}</span>
              <button onClick={() => setCount(Math.min(4, count + 1))} data-testid="count-plus" className="w-7 h-7 rounded-full border border-gold/60 text-gold flex items-center justify-center"><Plus size={15} /></button>
            </div>
            <span className="label-mono mt-1.5 text-[10px]">Number of outputs</span>
          </div>
          <div className="flex-1 flex flex-col items-center">
            <button onClick={generate} disabled={busy} data-testid="generate-video-submit"
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-goldlight to-gold text-ink-950 font-heading font-black text-2xl py-3 hover:brightness-105 transition disabled:opacity-60 shadow-[0_0_24px_rgba(198,138,91,0.35)]">
              {busy ? <Loader2 className="animate-spin" size={22} /> : (
                <><Sparkle size={22} />{isAdmin ? <span>Free</span> : <span className="flex items-baseline gap-2">{total}<span className="text-ink-950/50 text-base line-through font-bold">{total * 2}</span></span>}</>
              )}
            </button>
            <span className="label-mono mt-1.5 text-[10px]">Credits to generate</span>
          </div>
        </div>
        <p className="text-center text-xs text-zinc-600 pb-4 pt-1">AI-generated content. Illegal use prohibited.</p>
      </DialogContent>
    </Dialog>
  );
}
